import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Pencil, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import EmployeeDrawer from "@/components/EmployeeDrawer";
import BulkImportPanel from "@/components/BulkImportPanel";
import { EMPLOYEES, BULK_CSV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const TAX_STATUS_LABEL = {
    resident_decl: "Resident + Decl.",
    resident_no_decl: "Resident (no decl.)",
    non_resident: "Non-resident",
};

function fmtRate(v) {
    if (v == null || v === "") return "—";
    return `K${Number(v).toFixed(2)}`;
}

export default function Employees() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [drawerEmp, setDrawerEmp] = useState(null); // employee obj or "__new__"
    const [bulkOpen, setBulkOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employees", {
                params: { include_inactive: showInactive },
            });
            setRows(data);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

    const filtered = useMemo(() => {
        if (!q.trim()) return rows;
        const needle = q.trim().toLowerCase();
        return rows.filter(
            (r) =>
                r.name.toLowerCase().includes(needle) ||
                (r.position || "").toLowerCase().includes(needle) ||
                (r.employee_number || "").toLowerCase().includes(needle),
        );
    }, [rows, q]);

    return (
        <div data-testid={EMPLOYEES.page} className="p-10 max-w-[1400px]">
            {/* Header */}
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                        Employees master
                    </div>
                    <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                        {rows.length} people on the books
                    </h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        {isAdmin
                            ? "Edit rates, dependants, tax status, allowances, and bank details."
                            : "Read-only view — only admins can add or edit employees."}
                    </p>
                </div>
                {isAdmin && (
                    <div className="flex items-center gap-2">
                        <button
                            data-testid={BULK_CSV.openBtn}
                            onClick={() => setBulkOpen(true)}
                            className="inline-flex items-center gap-2 px-3 py-2.5 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border rounded-[3px]"
                            style={{ borderColor: "var(--kp-red)", color: "var(--kp-red)" }}
                        >
                            <Upload size={14} strokeWidth={2.2} /> Bulk import
                        </button>
                        <button
                            data-testid={EMPLOYEES.addBtn}
                            onClick={() => setDrawerEmp("__new__")}
                            className="inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px]"
                            style={{ background: "var(--kp-red)" }}
                        >
                            <Plus size={16} strokeWidth={2.2} /> New employee
                        </button>
                    </div>
                )}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-4 mb-4">
                <div className="relative w-80">
                    <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                    />
                    <input
                        data-testid={EMPLOYEES.searchInput}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="kp-focus w-full border border-neutral-300 pl-9 pr-3 py-2 text-sm bg-white rounded-[3px]"
                        placeholder="Search name, position, no."
                    />
                </div>
                <label className="flex items-center gap-2 text-xs text-neutral-600 select-none">
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        className="accent-[color:var(--kp-red)]"
                    />
                    Show inactive
                </label>
            </div>

            {/* Table */}
            <div className="bg-white border border-neutral-200 rounded-[3px] overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr
                            className="text-left text-[10px] uppercase tracking-[0.16em] text-neutral-500 font-display"
                            style={{ background: "#FAFAFA" }}
                        >
                            <th className="px-5 py-3 font-semibold">Name</th>
                            <th className="px-5 py-3 font-semibold">Position</th>
                            <th className="px-5 py-3 font-semibold text-right">Rate K/hr</th>
                            <th className="px-5 py-3 font-semibold text-center">Deps</th>
                            <th className="px-5 py-3 font-semibold">Tax status</th>
                            <th className="px-5 py-3 font-semibold text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-5 py-10 text-center text-neutral-400 text-xs">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-5 py-10 text-center text-neutral-400 text-xs">
                                    No employees match.
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            filtered.map((emp) => (
                                <tr
                                    key={emp.id}
                                    data-testid={EMPLOYEES.row(emp.id)}
                                    className={`border-t border-neutral-100 hover:bg-neutral-50 ${isAdmin ? "cursor-pointer" : ""}`}
                                    onClick={() => isAdmin && setDrawerEmp(emp)}
                                >
                                    <td className="px-5 py-3">
                                        <div className="text-[color:var(--kp-charcoal)] font-medium">
                                            {emp.name}
                                        </div>
                                        {!emp.active && (
                                            <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-400 font-display mt-0.5">
                                                Inactive
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 text-neutral-600">
                                        {emp.position || "—"}
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono-money">
                                        {fmtRate(emp.labour_rate_kina)}
                                    </td>
                                    <td className="px-5 py-3 text-center font-mono-money">
                                        {emp.dependants_declared ?? emp.dependants ?? 0}
                                    </td>
                                    <td className="px-5 py-3 text-neutral-600">
                                        {TAX_STATUS_LABEL[emp.tax_status] || emp.tax_status}
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        {isAdmin ? (
                                            <button
                                                data-testid={EMPLOYEES.editBtn(emp.id)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDrawerEmp(emp);
                                                }}
                                                className="inline-flex items-center gap-1 text-[12px] font-display font-semibold uppercase tracking-[0.12em]"
                                                style={{ color: "var(--kp-red)" }}
                                            >
                                                <Pencil size={12} /> Edit
                                            </button>
                                        ) : (
                                            <span className="text-[11px] text-neutral-400 font-display uppercase tracking-[0.12em]">
                                                view only
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            <EmployeeDrawer
                open={drawerEmp !== null}
                employee={drawerEmp === "__new__" ? null : drawerEmp}
                onClose={() => setDrawerEmp(null)}
                onSaved={() => {
                    setDrawerEmp(null);
                    load();
                }}
            />
            <BulkImportPanel
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onImported={() => { setBulkOpen(false); load(); }}
            />
        </div>
    );
}
